export class ApiError extends Error {
  status: number
  body: string
  constructor(status: number, body: string) {
    super(`API ${status}`)
    this.status = status
    this.body = body
  }
}

interface ClientOpts {
  getToken: () => Promise<string>
  fetchImpl?: typeof fetch
  // Self-heal hook for an unrecoverable 401. Invoked BEFORE the ApiError is
  // thrown; if it returns true the dead session was cleaned up and the caller
  // is being redirected to the login screen.
  onUnauthorized?: () => boolean
}

export function createApiClient({ getToken, fetchImpl = fetch, onUnauthorized }: ClientOpts) {
  async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = await getToken()
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` }
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    const res = await fetchImpl(path, { method, headers, body: body !== undefined ? JSON.stringify(body) : undefined })
    if (!res.ok) {
      if (res.status === 401) onUnauthorized?.()
      throw new ApiError(res.status, await res.text().catch(() => ''))
    }
    if (res.status === 204) return undefined as T
    const ct = res.headers.get('content-type') ?? ''
    return (ct.includes('application/json') ? await res.json() : (await res.text())) as T
  }
  return {
    get: <T>(p: string) => req<T>('GET', p),
    post: <T>(p: string, b?: unknown) => req<T>('POST', p, b),
    put: <T>(p: string, b?: unknown) => req<T>('PUT', p, b),
    patch: <T>(p: string, b?: unknown) => req<T>('PATCH', p, b),
    del: <T>(p: string) => req<T>('DELETE', p),
  }
}

import { acquireApiToken } from '../auth/tokenInterceptor'
import { hasOtpToken, clearOtpToken } from '../auth/otp/otpActions'
import { pca, apiScopes } from '../auth/msalConfig'

// Zentrale 401-Selbstheilung (V1.14 OTP + #172 Entra):
// Ein toter Login erzeugt sonst eine Sackgasse — useIsLoggedIn() bleibt true,
// RequireAuth lässt durch, aber jeder Call ist 401 und die App landet auf /denied.
// - OTP-Session (OTP-Token vorhanden): totes Token löschen, zurück zum Login.
// - Entra-Session (MSAL-Account vorhanden, Token von der API abgelehnt): das ist eine
//   abgelaufene/verworfene Session, KEIN „Konto nicht zugelassen" — ein voller
//   interaktiver Redirect holt eine frische Session, statt auf /denied zu enden (#172).
//   Früher wurde hier für Entra nichts getan (Annahme: MSAL-Refresh fängt das ab),
//   was genau die /denied-Sackgasse erzeugte, die nur ein Cache-Wipe löste.
export function healUnauthorized(): boolean {
  if (hasOtpToken()) {
    clearOtpToken()
    window.location.assign('/')
    return true
  }
  const account = pca?.getActiveAccount?.() ?? pca?.getAllAccounts?.()[0]
  if (account) {
    void pca.acquireTokenRedirect({ scopes: apiScopes, account })
    return true
  }
  return false
}

export const api = createApiClient({ getToken: acquireApiToken, onUnauthorized: healUnauthorized })
